import React from "react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, ComposedChart,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const COLORS = ["#4A90D9", "#F5A623", "#5CB85C", "#D0021B", "#9013FE", "#50E3C2", "#B8E986", "#F8E71C"];

const Wrap = ({ children, title }) => (
  <div className="my-4 p-3 sm:p-4 bg-slate-50 border border-slate-200 rounded-xl overflow-x-auto" data-testid="question-diagram">
    {title && <p className="text-center text-sm font-semibold text-slate-700 mb-2">{title}</p>}
    {children}
  </div>
);

/* ---------- Tables ---------- */
const DataTable = ({ d }) => (
  <table className="mx-auto text-sm border-collapse">
    <thead>
      <tr>
        <th className="border border-slate-300 px-3 py-1.5 bg-slate-100"></th>
        {d.col_labels.map((c) => (
          <th key={c} className="border border-slate-300 px-3 py-1.5 bg-slate-100 font-semibold">{c}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {d.row_labels.map((r, ri) => (
        <tr key={r}>
          <td className="border border-slate-300 px-3 py-1.5 font-medium bg-slate-50">{r}</td>
          {d.data[ri].map((v, ci) => (
            <td key={ci} className="border border-slate-300 px-3 py-1.5 text-center">{v}</td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

const DataTablePair = ({ d }) => (
  <div className="flex flex-wrap gap-6 justify-center">
    {[["data1", "label1"], ["data2", "label2"]].map(([dk, lk]) => (
      <DataTable key={dk} d={{ row_labels: d.row_labels, col_labels: d.col_labels, data: d[dk] }} title={d[lk]} />
    ))}
  </div>
);

/* ---------- Recharts graphs ---------- */
const RC = ({ children, h = 300 }) => (
  <ResponsiveContainer width="100%" height={h} minWidth={320}>{children}</ResponsiveContainer>
);

const BarGraph = ({ d }) => {
  const data = d.labels.map((l, i) => ({ name: l, value: d.values[i] }));
  return (
    <RC>
      <BarChart data={data} margin={{ top: 10, right: 10, bottom: 30, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" fontSize={12} /><YAxis fontSize={12} />
        <Tooltip /><Bar dataKey="value" name={d.unit || "value"} fill={COLORS[0]} />
      </BarChart>
    </RC>
  );
};

const BarGraphPair = ({ d }) => {
  const data = d.labels.map((l, i) => ({ name: l, [d.label1 || "A"]: d.values1[i], [d.label2 || "B"]: d.values2[i] }));
  return (
    <RC>
      <BarChart data={data} margin={{ top: 10, right: 10, bottom: 20, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" fontSize={12} /><YAxis fontSize={12} />
        <Tooltip /><Legend />
        <Bar dataKey={d.label1 || "A"} fill={COLORS[0]} /><Bar dataKey={d.label2 || "B"} fill={COLORS[1]} />
      </BarChart>
    </RC>
  );
};

const MultiBarGraph = ({ d }) => {
  // x-axis = col_labels (e.g. years); one bar per row_label (e.g. stores)
  const data = d.col_labels.map((c, ci) => {
    const row = { name: c };
    d.row_labels.forEach((r, ri) => { row[r] = d.data[ri][ci]; });
    return row;
  });
  return (
    <RC>
      <BarChart data={data} margin={{ top: 10, right: 10, bottom: 20, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" fontSize={12} /><YAxis fontSize={12} />
        <Tooltip /><Legend />
        {d.row_labels.map((r, i) => <Bar key={r} dataKey={r} fill={COLORS[i % COLORS.length]} />)}
      </BarChart>
    </RC>
  );
};

const StackedBarGraph = ({ d }) => {
  const data = d.categories.map((c, ci) => {
    const row = { name: c };
    d.components.forEach((comp, i) => { row[comp] = d.data[ci][i]; });
    return row;
  });
  return (
    <RC>
      <BarChart data={data} margin={{ top: 10, right: 10, bottom: 20, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" fontSize={12} /><YAxis fontSize={12} />
        <Tooltip /><Legend />
        {d.components.map((comp, i) => <Bar key={comp} dataKey={comp} stackId="a" fill={COLORS[i % COLORS.length]} />)}
      </BarChart>
    </RC>
  );
};

const SingleLine = ({ d }) => {
  const data = d.labels.map((l, i) => ({ name: l, value: d.values[i] }));
  return (
    <RC>
      <LineChart data={data} margin={{ top: 10, right: 20, bottom: 20, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" fontSize={12} /><YAxis fontSize={12} />
        <Tooltip /><Line type="monotone" dataKey="value" name={d.series_name || d.unit || "value"} stroke={COLORS[0]} strokeWidth={2} dot />
      </LineChart>
    </RC>
  );
};

const MultiLine = ({ d }) => {
  const names = Object.keys(d.series || {});
  const data = d.labels.map((l, i) => {
    const row = { name: l };
    names.forEach((n) => { row[n] = d.series[n][i]; });
    return row;
  });
  return (
    <RC>
      <LineChart data={data} margin={{ top: 10, right: 20, bottom: 20, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" fontSize={12} /><YAxis fontSize={12} />
        <Tooltip /><Legend />
        {names.map((n, i) => <Line key={n} type="monotone" dataKey={n} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot />)}
      </LineChart>
    </RC>
  );
};

const BarLineCombined = ({ d }) => {
  const data = d.labels.map((l, i) => ({ name: l, bar: d.bar_values[i], line: d.line_values[i] }));
  return (
    <RC>
      <ComposedChart data={data} margin={{ top: 10, right: 20, bottom: 20, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" fontSize={12} />
        <YAxis yAxisId="l" fontSize={12} /><YAxis yAxisId="r" orientation="right" fontSize={12} />
        <Tooltip /><Legend />
        <Bar yAxisId="l" dataKey="bar" name={d.bar_label || "bar"} fill={COLORS[0]} />
        <Line yAxisId="r" type="monotone" dataKey="line" name={d.line_label || "line"} stroke={COLORS[3]} strokeWidth={2} dot />
      </ComposedChart>
    </RC>
  );
};

const PieBase = ({ categories, percentages, title }) => {
  const data = categories.map((c, i) => ({ name: c, value: percentages[i] }));
  return (
    <div>
      {title && <p className="text-center text-xs font-medium text-slate-600 mb-1">{title}</p>}
      <RC h={280}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90}
               label={({ name, value }) => `${name}: ${value}%`} labelLine={false} fontSize={11}>
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip />
        </PieChart>
      </RC>
    </div>
  );
};

const PieChartPair = ({ d }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    <PieBase categories={d.categories} percentages={d.percentages1} title={`${d.title || ""} (Total ${d.total1})`} />
    <PieBase categories={d.categories} percentages={d.percentages2} title={`${d.title || ""} (Total ${d.total2})`} />
  </div>
);

const PieWithTable = ({ d }) => (
  <div className="grid sm:grid-cols-2 gap-4 items-center">
    <PieBase categories={d.categories} percentages={d.percentages} title={`${d.title || ""}${d.total ? ` (Total ${d.total})` : ""}`} />
    <table className="mx-auto text-sm border-collapse">
      <thead><tr>
        <th className="border border-slate-300 px-3 py-1.5 bg-slate-100">Category</th>
        <th className="border border-slate-300 px-3 py-1.5 bg-slate-100">{d.table_label || "Value"}</th>
      </tr></thead>
      <tbody>
        {d.categories.map((c, i) => (
          <tr key={c}>
            <td className="border border-slate-300 px-3 py-1.5">{c}</td>
            <td className="border border-slate-300 px-3 py-1.5 text-center">{d.table_values[i]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TablePieCombined = ({ d }) => (
  <div className="grid sm:grid-cols-2 gap-4 items-center">
    <table className="mx-auto text-sm border-collapse">
      <thead><tr>
        <th className="border border-slate-300 px-3 py-1.5 bg-slate-100">Period</th>
        <th className="border border-slate-300 px-3 py-1.5 bg-slate-100">Total {d.base_label || ""}</th>
      </tr></thead>
      <tbody>
        {d.periods.map((p, i) => (
          <tr key={p}>
            <td className="border border-slate-300 px-3 py-1.5">{p}</td>
            <td className="border border-slate-300 px-3 py-1.5 text-center">{d.totals[i]}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <PieBase categories={d.categories} percentages={d.percentages} title={`Breakdown for ${d.pie_period}`} />
  </div>
);

const CaseletGraph = ({ d }) => (
  <div>
    {d.caselet_text && <p className="text-sm text-slate-700 mb-2">{d.caselet_text}</p>}
    <PieBase categories={d.categories} percentages={d.percentages} title={d.cat_label} />
  </div>
);

/* ---------- SVG seating ---------- */
const ARROW = (
  <defs><marker id="ah" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
    <polygon points="0 0,6 3,0 6" fill="#333" /></marker></defs>
);
const seatFill = (name, hi, ans, reveal) =>
  (name === hi ? "#4A90D9" : (reveal && name === ans) ? "#5CB85C" : "#DDD");

const LinearSeating = ({ d, reveal }) => {
  const people = d.people, facings = d.facings || {}, W = 520, H = 200, m = 50;
  const spacing = people.length > 1 ? (W - 2 * m) / (people.length - 1) : 0, y = H / 2;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="mx-auto max-w-full" style={{ maxHeight: 220 }} fontFamily="Arial">
      {ARROW}
      <line x1={m - 20} y1={y + 40} x2={W - m + 20} y2={y + 40} stroke="#999" strokeWidth="2" />
      <text x={m - 40} y={y + 44} fontSize="11" fill="#999">Left</text>
      <text x={W - m + 22} y={y + 44} fontSize="11" fill="#999">Right</text>
      {people.map((name, i) => {
        const x = m + i * spacing;
        const f = facings[name];
        return (
          <g key={i}>
            <circle cx={x} cy={y} r="20" fill={seatFill(name, d.highlight, d.answer, reveal)} stroke="#333" strokeWidth="1.5" />
            <text x={x} y={y - 30} textAnchor="middle" fontSize="13" fontWeight="bold">{name}</text>
            {f && <line x1={x} y1={y} x2={x} y2={y + (f === "N" ? -14 : 14)} stroke="#333" strokeWidth="2" markerEnd="url(#ah)" />}
          </g>
        );
      })}
    </svg>
  );
};

const DoubleRowSeating = ({ d, reveal }) => {
  const W = 520, rowH = 90, H = 200, m = 50;
  const render = (arr, y, tag) => {
    const spacing = arr.length > 1 ? (W - 2 * m) / (arr.length - 1) : 0;
    return (
      <g>
        <text x={10} y={y + 4} fontSize="11" fill="#666">{tag}</text>
        {arr.map((name, i) => {
          const x = m + i * spacing;
          return (
            <g key={name}>
              <circle cx={x} cy={y} r="18" fill={seatFill(name, d.highlight, d.answer, reveal)} stroke="#333" strokeWidth="1.5" />
              <text x={x} y={y - 26} textAnchor="middle" fontSize="12" fontWeight="bold">{name}</text>
            </g>
          );
        })}
      </g>
    );
  };
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="mx-auto max-w-full" style={{ maxHeight: 220 }} fontFamily="Arial">
      {render(d.row_a, rowH * 0.7, "Row A")}
      {render(d.row_b, H - rowH * 0.5, "Row B")}
    </svg>
  );
};

const CircularSeating = ({ d, square, reveal }) => {
  const people = d.people, n = people.length, W = 400, H = 400, cx = W / 2, cy = H / 2;
  const seatR = Math.min(W, H) * 0.4, tR = Math.min(W, H) * 0.28;
  const labels = d.labels || [];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="mx-auto max-w-full" style={{ maxHeight: 360 }} fontFamily="Arial">
      {ARROW}
      {square
        ? <rect x={cx - tR} y={cy - tR} width={tR * 2} height={tR * 2} fill="#F0EAD6" stroke="#8B7355" strokeWidth="2" />
        : <circle cx={cx} cy={cy} r={tR} fill="#F0EAD6" stroke="#8B7355" strokeWidth="2" />}
      <text x={cx} y={cy + 5} textAnchor="middle" fontSize="11" fill="#8B7355">TABLE</text>
      {people.map((name, i) => {
        const th = (i * 2 * Math.PI) / n;
        const sx = cx + seatR * Math.sin(th), sy = cy - seatR * Math.cos(th);
        const facesCenter = d.facings_center ? d.facings_center[name] : d.facing === "center";
        let dx = facesCenter ? cx - sx : sx - cx, dy = facesCenter ? cy - sy : sy - cy;
        const dist = Math.hypot(dx, dy) || 1; dx /= dist; dy /= dist;
        const isCorner = labels[i] === "corner";
        const lr = seatR + 30, lx = cx + lr * Math.sin(th), ly = cy - lr * Math.cos(th);
        return (
          <g key={i}>
            {square && isCorner
              ? <rect x={sx - 17} y={sy - 17} width="34" height="34" fill={seatFill(name, d.highlight, d.answer, reveal)} stroke="#333" strokeWidth="1.5" />
              : <circle cx={sx} cy={sy} r="18" fill={seatFill(name, d.highlight, d.answer, reveal)} stroke="#333" strokeWidth="1.5" />}
            <line x1={sx} y1={sy} x2={sx + dx * 17} y2={sy + dy * 17} stroke="#333" strokeWidth="1.5" markerEnd="url(#ah)" />
            <text x={lx} y={ly} textAnchor="middle" fontSize="12" fontWeight="bold">{name}</text>
          </g>
        );
      })}
      {square && <text x={10} y={15} fontSize="10" fill="#666">Square = corner, Circle = side-middle</text>}
    </svg>
  );
};

const ConcentricSeating = ({ d, reveal }) => {
  const W = 420, H = 420, cx = W / 2, cy = H / 2;
  const ring = (arr, r, color) => arr.map((name, i) => {
    const th = (i * 2 * Math.PI) / arr.length;
    const x = cx + r * Math.sin(th), y = cy - r * Math.cos(th);
    return (
      <g key={name + r}>
        <circle cx={x} cy={y} r="16" fill={name === d.highlight ? "#4A90D9" : (reveal && name === d.answer) ? "#5CB85C" : color} stroke="#333" strokeWidth="1.5" />
        <text x={x} y={y - 22} textAnchor="middle" fontSize="11" fontWeight="bold">{name}</text>
      </g>
    );
  });
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="mx-auto max-w-full" style={{ maxHeight: 380 }} fontFamily="Arial">
      <circle cx={cx} cy={cy} r={W * 0.42} fill="none" stroke="#ccc" strokeDasharray="4 4" />
      <circle cx={cx} cy={cy} r={W * 0.24} fill="none" stroke="#ccc" strokeDasharray="4 4" />
      {ring(d.outer, W * 0.42, "#E8E8E8")}
      {ring(d.inner, W * 0.24, "#DDD")}
      <text x={cx} y={cy} textAnchor="middle" fontSize="10" fill="#999">inner / outer</text>
    </svg>
  );
};

/* ---------- Non-verbal reasoning ---------- */
const MirrorWord = ({ d, reveal }) => (
  <div className="text-center">
    <p className="text-xs text-slate-500 mb-1">Original</p>
    <p className="text-2xl font-mono font-bold tracking-widest">{d.original}</p>
    <p className="text-xs text-slate-500 mt-3 mb-1">{d.transform_kind === "water" ? "Water reflection" : "Mirror image"}</p>
    {reveal ? (
      <p className="text-2xl font-mono font-bold tracking-widest" style={{ transform: d.transform_kind === "water" ? "scaleY(-1)" : "scaleX(-1)", display: "inline-block" }}>{d.original}</p>
    ) : (
      <p className="text-2xl font-mono font-bold text-slate-300">?</p>
    )}
  </div>
);

const PaperFold = ({ d, reveal }) => {
  const g = d.grid_size || 12, S = 160, sc = S / g;
  const Box = ({ pts, title }) => (
    <div className="text-center">
      <p className="text-xs text-slate-600 mb-1">{title}</p>
      <svg viewBox={`0 0 ${S} ${S}`} width="160" height="160" className="border border-slate-300 bg-white">
        {pts.map((p, i) => <circle key={i} cx={p[0] * sc} cy={S - p[1] * sc} r="6" fill="#D0021B" />)}
      </svg>
    </div>
  );
  return (
    <div className="flex flex-wrap gap-6 justify-center">
      <Box pts={d.punch_position ? [d.punch_position] : []} title="Folded + Punched" />
      {reveal ? (
        <Box pts={d.resulting_holes || []} title={`Unfolded (${(d.resulting_holes || []).length} holes)`} />
      ) : (
        <div className="text-center">
          <p className="text-xs text-slate-600 mb-1">Unfolded = ?</p>
          <div className="w-40 h-40 border border-dashed border-slate-300 bg-white flex items-center justify-center text-3xl text-slate-300">?</div>
        </div>
      )}
    </div>
  );
};

const polygonPoints = (sides, cx, cy, r, rot) => {
  const pts = [];
  for (let i = 0; i < sides; i++) {
    const a = (i * 2 * Math.PI) / sides - Math.PI / 2 + (rot * Math.PI) / 180;
    pts.push(`${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`);
  }
  return pts.join(" ");
};

const ShapeSequence = ({ d }) => (
  <div className="flex flex-wrap gap-4 justify-center items-center">
    {(d.shapes || []).map((s, i) => (
      <svg key={i} viewBox="0 0 80 80" width="80" height="80">
        <polygon points={polygonPoints(s.sides || 3, 40, 40, 30, s.rotation || 0)}
          fill={s.fill === "solid" ? "#4A90D9" : "none"} stroke="#333" strokeWidth="2" />
      </svg>
    ))}
    <svg viewBox="0 0 80 80" width="80" height="80"><text x="40" y="48" textAnchor="middle" fontSize="40" fill="#999">?</text></svg>
  </div>
);

const VennStatement = ({ d }) => {
  const types = { A: "All", E: "No", I: "Some", O: "Some ... are not" };
  return (
    <div className="text-center">
      <svg viewBox="0 0 240 140" width="240" height="140" className="mx-auto">
        <circle cx="90" cy="70" r="55" fill="#4A90D9" fillOpacity="0.25" stroke="#4A90D9" strokeWidth="2" />
        <circle cx="150" cy="70" r="55" fill="#F5A623" fillOpacity="0.25" stroke="#F5A623" strokeWidth="2" />
        <text x="65" y="74" textAnchor="middle" fontSize="13" fontWeight="bold">{d.subject}</text>
        <text x="175" y="74" textAnchor="middle" fontSize="13" fontWeight="bold">{d.predicate}</text>
      </svg>
      <p className="text-sm text-slate-600 mt-1">Statement ({d.statement_type}): {types[d.statement_type] || ""} <b>{d.subject}</b> are <b>{d.predicate}</b></p>
    </div>
  );
};

/* ---------- Venn set-counting (shown in QUESTION — visualises given data) ---------- */
const VennCounting = ({ d }) => {
  if (d.type === "venn_diagram_2circle") {
    const a = d.circle_a || {}, b = d.circle_b || {};
    return (
      <div className="text-center">
        <svg viewBox="0 0 300 210" width="300" height="210" className="mx-auto">
          <circle cx="115" cy="105" r="75" fill="#4A90D9" fillOpacity="0.2" stroke="#4A90D9" strokeWidth="2" />
          <circle cx="185" cy="105" r="75" fill="#F5A623" fillOpacity="0.2" stroke="#F5A623" strokeWidth="2" />
          <text x="75" y="55" textAnchor="middle" fontSize="13" fontWeight="bold" fill="#2b6cb0">{a.label}</text>
          <text x="225" y="55" textAnchor="middle" fontSize="13" fontWeight="bold" fill="#b7791f">{b.label}</text>
          <text x="78" y="112" textAnchor="middle" fontSize="16" fontWeight="bold">{a.only_value}</text>
          <text x="150" y="112" textAnchor="middle" fontSize="16" fontWeight="bold">{d.overlap_value}</text>
          <text x="222" y="112" textAnchor="middle" fontSize="16" fontWeight="bold">{b.only_value}</text>
        </svg>
        <p className="text-xs text-slate-500">Total: <b>{d.total}</b>{d.neither_value != null && <> · Neither: <b>{d.neither_value}</b></>}</p>
      </div>
    );
  }
  if (d.type === "venn_diagram_3circle" && d.circle_b) {
    const a = d.circle_a || {}, b = d.circle_b || {}, c = d.circle_c || {};
    return (
      <div className="text-center">
        <svg viewBox="0 0 320 310" width="320" height="310" className="mx-auto">
          <circle cx="120" cy="120" r="85" fill="#4A90D9" fillOpacity="0.18" stroke="#4A90D9" strokeWidth="2" />
          <circle cx="200" cy="120" r="85" fill="#F5A623" fillOpacity="0.18" stroke="#F5A623" strokeWidth="2" />
          <circle cx="160" cy="195" r="85" fill="#5CB85C" fillOpacity="0.18" stroke="#5CB85C" strokeWidth="2" />
          <text x="70" y="50" textAnchor="middle" fontSize="13" fontWeight="bold" fill="#2b6cb0">{a.label}</text>
          <text x="250" y="50" textAnchor="middle" fontSize="13" fontWeight="bold" fill="#b7791f">{b.label}</text>
          <text x="160" y="300" textAnchor="middle" fontSize="13" fontWeight="bold" fill="#3f8c3f">{c.label}</text>
          <text x="85" y="100" textAnchor="middle" fontSize="15" fontWeight="bold">{a.only_value}</text>
          <text x="235" y="100" textAnchor="middle" fontSize="15" fontWeight="bold">{b.only_value}</text>
          <text x="160" y="240" textAnchor="middle" fontSize="15" fontWeight="bold">{c.only_value}</text>
          <text x="160" y="95" textAnchor="middle" fontSize="14" fontWeight="bold">{d.ab_only}</text>
          <text x="110" y="180" textAnchor="middle" fontSize="14" fontWeight="bold">{d.ac_only}</text>
          <text x="210" y="180" textAnchor="middle" fontSize="14" fontWeight="bold">{d.bc_only}</text>
          <text x="160" y="150" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#d0021b">{d.all_three}</text>
        </svg>
        <p className="text-xs text-slate-500">Total: <b>{d.total}</b>{d.none_value != null && <> · None: <b>{d.none_value}</b></>}</p>
      </div>
    );
  }
  // partial / fallback: region breakdown list
  const rows = Object.entries(d).filter(([k, v]) =>
    !["type", "title"].includes(k) && (typeof v === "number" || (v && typeof v === "object" && !Array.isArray(v))));
  return (
    <table className="w-full text-sm"><tbody>
      {rows.map(([k, v]) => (
        <tr key={k} className="border-b border-slate-100">
          <td className="py-1 pr-3 text-slate-600 capitalize">{k.replace(/_/g, " ")}</td>
          <td className="py-1 font-semibold text-slate-800">{typeof v === "object" ? Object.entries(v).map(([kk, vv]) => `${kk}: ${vv}`).join(", ") : v}</td>
        </tr>
      ))}
    </tbody></table>
  );
};

/* ---------- Syllogism venns (SOLUTION aid — reveal only) ---------- */
const VennSyllogism = ({ d }) => {
  const stmts = d.statements || [d.statement1, d.statement2].filter(Boolean);
  return (
    <div className="space-y-1.5">
      {d.terms && <p className="text-xs text-slate-500">Terms: <b>{d.terms.join(", ")}</b></p>}
      {stmts.map((s, i) => (
        <div key={i} className="rounded border border-indigo-100 bg-indigo-50/60 px-3 py-1.5 text-sm text-slate-700">
          <span className="text-indigo-600 font-semibold mr-1">S{i + 1}:</span>{s.text || s}
          {s.relationship && <span className="text-xs text-slate-400 ml-1">({s.relationship.type})</span>}
        </div>
      ))}
      {d.relationship_type && <p className="text-xs text-slate-500">Relationship: <b>{d.relationship_type.replace(/_/g, " ")}</b></p>}
    </div>
  );
};

/* ---------- Text-logic reasoning cards (SOLUTION aid — reveal only) ---------- */
const LOGIC_LABELS = {
  outcome: "Observed outcome", root_cause: "Root cause",
  statement_1: "Statement I", statement_2: "Statement II", classification: "Relation",
  statements: "Statements", combined_chain: "Combined (chain)",
  conditional: "Conditional", given: "Given", conclusion: "Valid conclusion",
  statement1: "Statement 1", statement2: "Statement 2",
  statement: "Statement", identified_assumption: "Assumption", claim: "Claim",
  problem: "Problem", recommendation: "Recommendation", candidate_assumption: "Candidate assumption", verdict: "Verdict",
  argument: "Argument", assumption_1: "Assumption 1", assumption_2: "Assumption 2", answer: "Answer",
};
const LogicCard = ({ d }) => {
  const skip = new Set(["type", "title", "assumption_1_necessary", "assumption_2_necessary"]);
  const entries = Object.entries(d).filter(([k, v]) => !skip.has(k) && v != null && v !== "");
  return (
    <div className="space-y-1.5 text-sm">
      {entries.map(([k, v]) => (
        <div key={k} className="flex gap-2">
          <span className="flex-shrink-0 font-semibold text-slate-500 w-28">{LOGIC_LABELS[k] || k.replace(/_/g, " ")}</span>
          <span className="text-slate-800">{Array.isArray(v) ? (
            <ol className="list-decimal ml-4">{v.map((x, i) => <li key={i}>{typeof x === "object" ? JSON.stringify(x) : x}</li>)}</ol>
          ) : String(v)}</span>
        </div>
      ))}
    </div>
  );
};

/* ---------- Family tree (SOLUTION aid — reveal only) ---------- */
const FamilyTree = ({ d }) => {
  const pc = d.parent_child || [];
  const marriages = d.marriages || [];
  const everyone = new Set();
  pc.forEach((e) => { everyone.add(e.parent); everyone.add(e.child); });
  marriages.forEach((m) => { everyone.add(m.person1); everyone.add(m.person2); });
  const children = new Set(pc.map((e) => e.child));
  // generation via longest path from roots
  const gen = {};
  [...everyone].forEach((p) => { if (!children.has(p)) gen[p] = 0; });
  let changed = true, guard = 0;
  while (changed && guard++ < 50) {
    changed = false;
    pc.forEach((e) => {
      const g = (gen[e.parent] ?? 0) + 1;
      if (gen[e.child] == null || g > gen[e.child]) { gen[e.child] = g; changed = true; }
    });
  }
  // align spouses to same gen
  marriages.forEach((m) => {
    const g = Math.max(gen[m.person1] ?? 0, gen[m.person2] ?? 0);
    gen[m.person1] = g; gen[m.person2] = g;
  });
  const byGen = {};
  [...everyone].forEach((p) => { (byGen[gen[p] ?? 0] = byGen[gen[p] ?? 0] || []).push(p); });
  const gens = Object.keys(byGen).map(Number).sort((a, b) => a - b);
  const W = 460, rowH = 78, boxW = 92, boxH = 34, H = gens.length * rowH + 20;
  const pos = {};
  gens.forEach((g, ri) => {
    const arr = byGen[g], step = W / (arr.length + 1);
    arr.forEach((p, i) => { pos[p] = { x: step * (i + 1), y: ri * rowH + 30 }; });
  });
  const isMarried = (a, b) => marriages.some((m) => (m.person1 === a && m.person2 === b) || (m.person1 === b && m.person2 === a));
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W }} className="mx-auto">
        {marriages.map((m, i) => {
          const p1 = pos[m.person1], p2 = pos[m.person2]; if (!p1 || !p2) return null;
          return <line key={"m" + i} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="#d0021b" strokeWidth="2" strokeDasharray="4 3" />;
        })}
        {pc.map((e, i) => {
          const pp = pos[e.parent], cp = pos[e.child]; if (!pp || !cp) return null;
          // draw from marriage midpoint if spouse exists
          let sx = pp.x;
          const spouse = marriages.find((m) => m.person1 === e.parent || m.person2 === e.parent);
          if (spouse) { const other = spouse.person1 === e.parent ? spouse.person2 : spouse.person1; if (pos[other] && isMarried(e.parent, other)) sx = (pp.x + pos[other].x) / 2; }
          return <path key={"c" + i} d={`M ${sx} ${pp.y + boxH / 2} L ${sx} ${(pp.y + cp.y) / 2} L ${cp.x} ${(pp.y + cp.y) / 2} L ${cp.x} ${cp.y - boxH / 2}`} fill="none" stroke="#94a3b8" strokeWidth="1.5" />;
        })}
        {[...everyone].map((p) => {
          const pt = pos[p]; if (!pt) return null;
          const isQ = d.query && (d.query.person === p || d.query.relative_to === p);
          return (
            <g key={p}>
              <rect x={pt.x - boxW / 2} y={pt.y - boxH / 2} width={boxW} height={boxH} rx="6"
                fill={isQ ? "#fef3c7" : "#eef2ff"} stroke={isQ ? "#f59e0b" : "#6366f1"} strokeWidth="1.5" />
              <text x={pt.x} y={pt.y + 4} textAnchor="middle" fontSize="12" fontWeight="bold" fill="#1e293b">{p}</text>
            </g>
          );
        })}
      </svg>
      <p className="text-xs text-slate-500 text-center mt-1">— marriage · │ parent→child</p>
      {d.query && <p className="text-sm text-center text-slate-700 mt-1"><b>{d.query.person}</b> is the <b className="text-emerald-700">{d.answer}</b> of <b>{d.query.relative_to}</b></p>}
    </div>
  );
};

/* ---------- Ranking order (SOLUTION aid — reveal only) ---------- */
const RankingOrder = ({ d }) => {
  const order = d.order_highest_to_lowest || d.order || [];
  return (
    <div>
      <p className="text-xs text-slate-500 mb-1.5">{(d.metric || "value")}: highest → lowest</p>
      <div className="flex flex-wrap items-center gap-1.5">
        {order.map((p, i) => (
          <React.Fragment key={i}>
            <span className="rounded-md bg-teal-50 border border-teal-200 px-2.5 py-1 text-sm font-medium text-teal-800">{i + 1}. {p}</span>
            {i < order.length - 1 && <span className="text-slate-400">›</span>}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

/* ---------- Schedule table (SOLUTION aid — reveal only) ---------- */
const ScheduleTable = ({ d }) => {
  const sch = d.schedule || {};
  return (
    <table className="w-full text-sm"><tbody>
      {Object.entries(sch).map(([person, slot]) => {
        const isAnchor = person === d.anchor_person;
        return (
          <tr key={person} className={`border-b border-slate-100 ${isAnchor ? "bg-amber-50" : ""}`}>
            <td className="py-1.5 pr-3 font-medium text-slate-700">{person}</td>
            <td className="py-1.5 text-slate-800">{slot}</td>
          </tr>
        );
      })}
    </tbody></table>
  );
};

/* ---------- Direction path (SOLUTION aid — reveal only) ---------- */
const DirectionPath = ({ d }) => {
  const coords = d.path_coordinates || [];
  if (coords.length < 2) return <LogicCard d={d} />;
  const xs = coords.map((c) => c[0]), ys = coords.map((c) => c[1]);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
  const pad = 30, span = 180;
  const rngX = maxX - minX || 1, rngY = maxY - minY || 1, sc = span / Math.max(rngX, rngY);
  const tx = (x) => pad + (x - minX) * sc;
  const ty = (y) => pad + span - (y - minY) * sc; // flip: north up
  const pts = coords.map((c) => `${tx(c[0])},${ty(c[1])}`).join(" ");
  const S = span + pad * 2;
  return (
    <div className="text-center">
      <svg viewBox={`0 0 ${S} ${S}`} width="240" height="240" className="mx-auto">
        <text x={S / 2} y="12" textAnchor="middle" fontSize="11" fill="#94a3b8">N</text>
        <text x={S / 2} y={S - 2} textAnchor="middle" fontSize="11" fill="#94a3b8">S</text>
        <text x="8" y={S / 2} textAnchor="middle" fontSize="11" fill="#94a3b8">W</text>
        <text x={S - 8} y={S / 2} textAnchor="middle" fontSize="11" fill="#94a3b8">E</text>
        <polyline points={pts} fill="none" stroke="#4A90D9" strokeWidth="2.5" markerEnd="url(#dparrow)" />
        <defs><marker id="dparrow" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto"><path d="M0,0 L8,4 L0,8 Z" fill="#4A90D9" /></marker></defs>
        <circle cx={tx(coords[0][0])} cy={ty(coords[0][1])} r="5" fill="#5CB85C" />
        <text x={tx(coords[0][0])} y={ty(coords[0][1]) - 8} textAnchor="middle" fontSize="10" fill="#3f8c3f">Start</text>
        <circle cx={tx(coords[coords.length - 1][0])} cy={ty(coords[coords.length - 1][1])} r="5" fill="#D0021B" />
        <text x={tx(coords[coords.length - 1][0])} y={ty(coords[coords.length - 1][1]) - 8} textAnchor="middle" fontSize="10" fill="#b7181d">End</text>
      </svg>
      {d.final_facing && <p className="text-xs text-slate-500">Final facing: <b>{d.final_facing}</b>{d.shadow_direction && <> · Shadow: <b>{d.shadow_direction}</b></>}</p>}
    </div>
  );
};

/* ---------- Sun / shadow & rotation compass (SOLUTION aid — reveal only) ---------- */
const DIR_ANGLE = { North: 0, "North-East": 45, East: 90, "South-East": 135, South: 180, "South-West": 225, West: 270, "North-West": 315 };
const CompassDir = ({ d }) => {
  const cx = 110, cy = 110, r = 78;
  const arrow = (dir, color, label) => {
    const a = DIR_ANGLE[dir]; if (a == null) return null;
    const rad = (a - 90) * Math.PI / 180;
    const x = cx + r * Math.cos(rad), y = cy + r * Math.sin(rad);
    return (
      <g key={label}>
        <line x1={cx} y1={cy} x2={x} y2={y} stroke={color} strokeWidth="3" />
        <circle cx={x} cy={y} r="4" fill={color} />
        <text x={cx + (r + 16) * Math.cos(rad)} y={cy + (r + 16) * Math.sin(rad) + 4} textAnchor="middle" fontSize="10" fontWeight="bold" fill={color}>{label}</text>
      </g>
    );
  };
  return (
    <div className="text-center">
      <svg viewBox="0 0 220 220" width="220" height="220" className="mx-auto">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth="1.5" />
        <text x={cx} y="16" textAnchor="middle" fontSize="11" fill="#94a3b8">N</text>
        <text x={cx} y="212" textAnchor="middle" fontSize="11" fill="#94a3b8">S</text>
        <text x="10" y={cy + 4} textAnchor="middle" fontSize="11" fill="#94a3b8">W</text>
        <text x="210" y={cy + 4} textAnchor="middle" fontSize="11" fill="#94a3b8">E</text>
        {arrow(d.sun_direction, "#F5A623", "Sun")}
        {arrow(d.shadow_direction, "#334155", "Shadow")}
        {arrow(d.new_facing || d.final_facing || d.initial_facing || d.start_facing, "#4A90D9", "Face")}
      </svg>
      <div className="text-xs text-slate-500 space-y-0.5">
        {d.time_of_day && <p>Time: <b className="capitalize">{d.time_of_day}</b></p>}
        {d.shadow_relative_position && <p>Shadow is <b>{d.shadow_relative_position}</b></p>}
      </div>
    </div>
  );
};

/* ---------- Row / line position (SOLUTION aid — reveal only) ---------- */
const RowLine = ({ d }) => {
  const total = d.total_people || d.derived_total || 0;
  const n = Math.min(total, 24);
  const mark = {};
  if (d.known_position) mark[d.known_side === "right" ? total - d.known_position : d.known_position - 1] = "K";
  const W = 320, dotR = 8, gap = n > 1 ? (W - 20) / (n - 1) : 0;
  return (
    <div className="text-center">
      {n > 0 && (
        <svg viewBox={`0 0 ${W} 40`} width="100%" style={{ maxWidth: W }} className="mx-auto">
          {Array.from({ length: n }).map((_, i) => (
            <circle key={i} cx={10 + i * gap} cy={20} r={dotR} fill={mark[i] ? "#4A90D9" : "#cbd5e1"} />
          ))}
          {total > n && <text x={W - 4} y={24} textAnchor="end" fontSize="10" fill="#94a3b8">…{total}</text>}
        </svg>
      )}
      <div className="text-xs text-slate-500 mt-1 space-y-0.5">
        {d.total_people != null && <p>Total: <b>{d.total_people}</b></p>}
        {d.derived_total != null && <p>Derived total: <b>{d.derived_total}</b></p>}
        {d.other_side_position != null && <p>Position from other side: <b>{d.other_side_position}</b></p>}
        {d.answer != null && <p>Answer: <b className="text-emerald-700">{d.answer}</b></p>}
      </div>
    </div>
  );
};

/* ---------- Square table seating (SOLUTION aid — reveal only) ---------- */
const SquareTableSeating = ({ d, reveal }) => {
  const people = d.people || [];
  const W = 340, m = 60, inner = W - 2 * m;
  // 8 seats: corners + side midpoints, clockwise starting top-left corner
  const seatPos = [
    [m, m], [W / 2, m], [W - m, m], [W - m, W / 2],
    [W - m, W - m], [W / 2, W - m], [m, W - m], [m, W / 2],
  ];
  return (
    <div className="text-center">
      <svg viewBox={`0 0 ${W} ${W}`} width="300" height="300" className="mx-auto">
        <rect x={m + 20} y={m + 20} width={inner - 40} height={inner - 40} rx="8" fill="#f1f5f9" stroke="#cbd5e1" />
        <text x={W / 2} y={W / 2 + 4} textAnchor="middle" fontSize="10" fill="#94a3b8">Table (facing center)</text>
        {people.slice(0, 8).map((name, i) => {
          const [x, y] = seatPos[i] || [W / 2, W / 2];
          return (
            <g key={i}>
              <circle cx={x} cy={y} r="22" fill={seatFill(name, null, d.answer, reveal)} stroke="#64748b" strokeWidth="1.5" />
              <text x={x} y={y + 3} textAnchor="middle" fontSize="10" fontWeight="bold" fill="#1e293b">{name}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};


const RENDERERS = {
  bar_graph: BarGraph, bar_graph_pair: BarGraphPair, multi_bar_graph: MultiBarGraph, stacked_bar_graph: StackedBarGraph,
  line_graph: SingleLine, multi_line_graph: MultiLine, bar_line_combined: BarLineCombined,
  pie_chart: (p) => <PieBase categories={p.d.categories} percentages={p.d.percentages} title={p.d.title} />,
  pie_chart_pair: PieChartPair, pie_with_table: PieWithTable, table_pie_combined: TablePieCombined, caselet_graph: CaseletGraph,
  data_table: DataTable, data_table_pair: DataTablePair,
  linear_seating: LinearSeating, double_row_seating: DoubleRowSeating,
  circular_seating: (p) => <CircularSeating d={p.d} reveal={p.reveal} />,
  square_seating: (p) => <CircularSeating d={p.d} square reveal={p.reveal} />,
  concentric_circular_seating: ConcentricSeating,
  mirror_word: MirrorWord, paper_fold: PaperFold, shape_sequence: ShapeSequence, venn_statement: VennStatement,
  // Venn set-counting (shown in question)
  venn_diagram_2circle: VennCounting, venn_diagram_3circle: VennCounting, venn_diagram_3circle_partial: VennCounting,
  // Syllogism venns (reveal only)
  venn_diagram: VennSyllogism, venn_diagram_2term: VennSyllogism, venn_diagram_multi: VennSyllogism, venn_diagram_structural: VennSyllogism,
  // Text-logic reasoning (reveal only)
  cause_effect_link: LogicCard, causal_relationship: LogicCard, propositional_chain: LogicCard,
  propositional_statement: LogicCard, propositional_pair: LogicCard, assumption_analysis: LogicCard,
  assumption_necessity_test: LogicCard, dual_assumption_test: LogicCard, stated_vs_implied: LogicCard,
  // Family tree (reveal only)
  family_tree: FamilyTree, family_tree_coded: FamilyTree,
  // Ranking (reveal only)
  ranking_order: RankingOrder,
  // Schedules (reveal only)
  day_schedule: ScheduleTable, month_schedule: ScheduleTable,
  // Direction / compass (reveal only)
  direction_path: DirectionPath, direction_path_with_shadow: DirectionPath, direction_rotation: CompassDir,
  sun_shadow: CompassDir, sun_shadow_after_turn: CompassDir, sun_shadow_relative: CompassDir,
  sun_shadow_reverse: CompassDir, sun_shadow_reverse_after_turn: CompassDir,
  // Row / line position (reveal only)
  row_position: RowLine, row_total_derivation: RowLine, row_gap_derivation: RowLine, row_position_swap: RowLine,
  // Square-table seating (reveal only)
  square_table_seating: SquareTableSeating,
};

/* Error boundary — a malformed diagram spec must never crash the practice page */
class DiagramErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { console.warn("DiagramRenderer failed:", err); }
  componentDidUpdate(prev) { if (prev.resetKey !== this.props.resetKey && this.state.failed) this.setState({ failed: false }); }
  render() { return this.state.failed ? null : this.props.children; }
}

/* These diagram types plot a DEDUCED arrangement (names in their solved
 * positions) — i.e. they ARE the answer. Only show them once the solution is
 * revealed; in question mode they'd give the puzzle away. */
const ANSWER_REVEALING_TYPES = new Set([
  "linear_seating", "double_row_seating", "circular_seating",
  "square_seating", "concentric_circular_seating", "square_table_seating",
  // Syllogism venns (show the deduced relationship)
  "venn_diagram", "venn_diagram_2term", "venn_diagram_multi", "venn_diagram_structural",
  // Text-logic reasoning (contain the solution/answer)
  "cause_effect_link", "causal_relationship", "propositional_chain",
  "propositional_statement", "propositional_pair", "assumption_analysis",
  "assumption_necessity_test", "dual_assumption_test", "stated_vs_implied",
  // Family tree, ranking, schedules, directions, row derivations (contain the answer)
  "family_tree", "family_tree_coded", "ranking_order",
  "day_schedule", "month_schedule",
  "direction_path", "direction_path_with_shadow", "direction_rotation",
  "sun_shadow", "sun_shadow_after_turn", "sun_shadow_relative",
  "sun_shadow_reverse", "sun_shadow_reverse_after_turn",
  "row_position", "row_total_derivation", "row_gap_derivation", "row_position_swap",
]);

const DiagramRenderer = ({ diagram, reveal = false }) => {
  if (!diagram || typeof diagram !== "object" || !diagram.type) return null;
  const Comp = RENDERERS[diagram.type];
  if (!Comp) return null;
  // Hide answer-revealing arrangement diagrams until the solution is shown.
  if (!reveal && ANSWER_REVEALING_TYPES.has(diagram.type)) return null;
  return (
    <DiagramErrorBoundary resetKey={diagram}>
      <Wrap title={diagram.unit && diagram.type !== "pie_chart" ? diagram.unit : null}>
        <Comp d={diagram} reveal={reveal} />
      </Wrap>
    </DiagramErrorBoundary>
  );
};

export default DiagramRenderer;
